"use client";

import { Button } from "@/components/ui/button";
import { RefreshCWIcon } from "@/components/ui/refresh-cw";
import { cn } from "@/lib/utils";
import {
  KB_STATUS_DOT,
  KB_STATUS_LABELS,
  type KbStatus,
  type KbStatusCounts,
} from "./kb-utils";

export type KbStatusFilter = "all" | KbStatus;

const FILTERS: { id: KbStatusFilter; label: string }[] = [
  { id: "all", label: "All" },
  { id: "ready", label: KB_STATUS_LABELS.ready },
  { id: "processing", label: KB_STATUS_LABELS.processing },
  { id: "failed", label: KB_STATUS_LABELS.failed },
];

const SEGMENTS: KbStatus[] = ["ready", "processing", "failed"];

type KbStatusBarProps = {
  counts: KbStatusCounts;
  active: KbStatusFilter;
  onChange: (filter: KbStatusFilter) => void;
  onRetryFailed: () => void;
  retrying: boolean;
};

export function KbStatusBar({
  counts,
  active,
  onChange,
  onRetryFailed,
  retrying,
}: KbStatusBarProps) {
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
      <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label="Filter by status">
        {FILTERS.map((filter) => {
          const count = counts[filter.id];
          const isActive = active === filter.id;
          return (
            <button
              key={filter.id}
              type="button"
              aria-pressed={isActive}
              onClick={() => onChange(filter.id)}
              className={cn(
                "flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                isActive
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-border bg-card text-muted-foreground hover:bg-accent/50 hover:text-foreground"
              )}
            >
              {filter.id === "all" ? null : (
                <span
                  className={cn("size-1.5 shrink-0 rounded-full", KB_STATUS_DOT[filter.id])}
                  aria-hidden
                />
              )}
              <span>{filter.label}</span>
              <span className="tabular-nums text-muted-foreground">{count}</span>
            </button>
          );
        })}
      </div>

      <div className="flex min-w-[140px] flex-1 items-center gap-3">
        <div
          className="flex h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-muted"
          role="img"
          aria-label={`${counts.ready} ready, ${counts.processing} processing, ${counts.failed} failed`}
        >
          {counts.all > 0
            ? SEGMENTS.map((segment) =>
                counts[segment] > 0 ? (
                  <span
                    key={segment}
                    className={cn("h-full", KB_STATUS_DOT[segment])}
                    style={{ width: `${(counts[segment] / counts.all) * 100}%` }}
                  />
                ) : null
              )
            : null}
        </div>

        {counts.failed > 0 ? (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="shrink-0 gap-1.5 text-muted-foreground hover:text-foreground"
            onClick={onRetryFailed}
            disabled={retrying}
          >
            <RefreshCWIcon className="size-4" size={16} />
            {retrying ? "Retrying…" : `Retry ${counts.failed} failed`}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
